import React, { useRef, useState } from 'react';
import { Badge, Box, Button, Collapse, makeStyles, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Toolbar, Typography } from '@material-ui/core';
import { FilterList } from '@material-ui/icons';
import TextFilter from './FilterComponents/TextFilter';
import SelectFilter from './FilterComponents/SelectFilter';
import DateFilter from './FilterComponents/DateFilter';
import CheckboxFilter from './FilterComponents/CheckboxFilter';
import DateTimeFilter from './FilterComponents/DateTimeFilter';

const useStyles = makeStyles((theme) => ({
  singleLineRow: {
    whiteSpace: 'nowrap',
  },
  clampRow: {
    textOverflow: 'ellipsis',
    overflow: 'hidden'
  },
  tableContainer: {
    overflowX: 'auto'
  },
  table: {
    tableLayout: 'auto',
  },
  tableHeader: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: '#F2F2F2'
  },
  tableHeadCell: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: '#F2F2F2'
  },
  toolbar: {
    flexDirection: 'column',
    padding: '1rem',
  },
  toolbarContent: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    width: '100%'
  },
  collapseContent: {
    backgroundColor: '#FFF',
    border: '1px solid #CCC',
    borderRadius: '0.25rem',
    padding: '1rem',
    marginTop: '1rem'
  },
  filtersContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '1rem',
    flexWrap: 'wrap'
  },
  checkboxContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: '2.5rem',
    flexWrap: 'wrap',
    paddingLeft: '0.2rem'
  },
  filterContainer: {
    display: 'flex',
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: '16rem'
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    paddingTop: '2rem'
  }
}));

/**
 * @param {object} props
 * @param {import('./interfaces').TableFilter} props.filter
 * @param {string} props.selectedValue
 */
const renderFilterComponent = ({ filter, selectedValue }) => {
  const commonProps = {
    name: filter.key,
    label: filter.name,
    placeholder: filter?.component?.placeholder,
    defaultValue: selectedValue,
    defaultChecked: selectedValue,
  }
  
  if(filter.component.type === 'text') {
    return <TextFilter {...commonProps} />
  }

  if(filter.component.type === 'date') {
    return <DateFilter {...commonProps} />
  }

  if(filter.component.type === 'select') {
    return <SelectFilter {...commonProps} options={filter?.component?.options} />
  }

  if(filter.component.type === 'checkbox') {
    return <CheckboxFilter {...commonProps} />
  }

  if(filter.component.type === 'datetime-local') {
    return <DateTimeFilter {...commonProps} />
  }

  return <TextFilter {...commonProps} />
}

const renderCustomToolbarElement = (CustomToolbarElement) => {
  if(typeof CustomToolbarElement === 'function') {
    return () => CustomToolbarElement;
  }

  return CustomToolbarElement;
}

/**
 * Componente para abstrair a criação de tabelas
 * @param {import('./interfaces').DataTableProps} props
 */
export default function DataTable(props) {
  const tableContainerRef = useRef(null);
  const classnames = useStyles();

  const [showFilters, setShowFilters] = useState(props?.defaultOpenedFilters || false);
  
  const columns = props?.columns || [];
  const filters = props?.filters || [];
  const rows = props?.rows || [];
  const standardFilters = filters.filter(f => f.component.type !== 'checkbox');
  const checkboxFilters = filters.filter(f => f.component.type === 'checkbox');

  const selectedFilters = typeof props?.selectedFilters === 'object' ? props.selectedFilters : {};
  const hasSelectedFilters = Object.keys(selectedFilters).length > 0;
  const isDenseTable = typeof props?.isDense !== 'undefined' ? props.isDense : true;
  const showPagination = typeof props?.showPagination !== 'undefined' ? props.showPagination : true;
  const tableRowsPerPage = props?.hideRowsPerPage ? [props?.rowsPerPage || 5] : [10, 50, 100];

  const resetTableContainerScroll = () => {
    if(tableContainerRef.current) {
      tableContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  const toggleShowFilters = () => {
    setShowFilters(prev => !prev);
  }

  /**
   * @param {React.FormEvent<HTMLFormElement>} e 
   */
  const handleFiltersSubmit = (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);

    for(const input of formData.keys()) {
      if(e.target[input]?.type === 'checkbox') {
        formData.set(input, true);
      }
    }
    
    typeof props?.onSubmitFilters === 'function' && props.onSubmitFilters(Object.fromEntries(formData));
    resetTableContainerScroll();
  }

  /**
   * @param {React.FormEvent<HTMLFormElement>} e 
   */
  const handleFiltersReset = (e) => {
    e.preventDefault();

    typeof props?.onResetFilters === 'function' && props.onResetFilters();
    resetTableContainerScroll();
  }

  const handlePageChange = (event, page) => {
    if(page >= 0) {
      typeof props?.onPageChange === 'function' && props.onPageChange(page);
      resetTableContainerScroll();
    }
  }

  const handleRowsPageChange = (e) => {
    typeof props?.onPageChange === 'function' && props.onRowsPerChange(parseInt(e.target.value, 10));
    resetTableContainerScroll();
  }

  return (
    <Paper>
      {props?.showFilters && (
        <Toolbar className={classnames.toolbar} variant="dense">
          <Box className={classnames.toolbarContent} justifyContent={props?.CustomToolbarElement ? "space-between" : "flex-end"}>
            {renderCustomToolbarElement(props?.CustomToolbarElement)}
            <Box display="flex" alignItems="flex-start">
              <Badge color="primary" invisible={!hasSelectedFilters}>
                <Button
                  variant={showFilters ? 'contained' : 'outlined'}
                  color="primary"
                  onClick={toggleShowFilters}
                >
                  <FilterList />
                  Filtros
                </Button>
              </Badge>
            </Box>
          </Box>
          <Box width="100%">
            <Collapse in={showFilters}>
              <Box component="form" className={classnames.collapseContent} onSubmit={handleFiltersSubmit} onReset={handleFiltersReset}>
                
                {/* 1º BLOCO: Campos de Texto, Data e Select */}
                {standardFilters.length > 0 && (
                  <Box className={classnames.filtersContainer}>
                    {standardFilters.map((filter) => {
                      return (
                        <Box key={`${filter.key}-${selectedFilters[filter?.key]}`} className={classnames.filterContainer}>
                          {renderFilterComponent({ filter, selectedValue: selectedFilters[filter?.key] })}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                {/* 2º BLOCO: Campos de Checkbox (Embaixo) */}
                {checkboxFilters.length > 0 && (
                  <Box 
                    className={classnames.checkboxContainer} 
                    style={{ marginTop: standardFilters.length > 0 ? '1.5rem' : '0' }}
                  >
                    {checkboxFilters.map((filter) => {
                      return (
                        <Box key={`${filter.key}-${selectedFilters[filter?.key]}`}>
                          {renderFilterComponent({ filter, selectedValue: selectedFilters[filter?.key] })}
                        </Box>
                      );
                    })}
                  </Box>
                )}

                <Box className={classnames.filterActions}>
                  {hasSelectedFilters && <Button variant="outlined" color="primary" type="reset">Limpar filtros</Button>}
                  <Button variant="contained" color="primary" type="submit">Aplicar filtros</Button>
                </Box>
              </Box>
            </Collapse>
          </Box>
        </Toolbar>
      )}
      <TableContainer ref={tableContainerRef} className={classnames.tableContainer} style={{ maxHeight: typeof props?.maxHeight === 'string' ? props.maxHeight : 'auto'}}>
        <Table
          stickyHeader={props?.hasStickHeader}
          className={classnames.table}
          size={isDenseTable ? "small" : "medium"}
        >
          <TableHead className={classnames.tableHeader}>
            <TableRow>
              {columns.map(column => {
                return (
                  <TableCell
                    className={[
                      (props?.isSingleLine || column?.isSingleLine) && classnames.singleLineRow,
                      classnames.tableHeadCell
                    ]}
                    align={column?.align}
                    key={column.key}
                    title={column.name}
                    style={{
                      maxWidth: column?.maxWidth ? column.maxWidth : 'auto',
                      width: column?.width ? column.width : 'auto',
                      color: '#fff'
                    }}
                  >
                    {column.name}
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {props?.isLoading && (
              Array(rows.length || props?.rowsPerPage || 10).fill(0).map((_, index) => {
                return (
                  <TableRow key={`loading-${index}`}>
                    <TableCell colSpan={columns.length}>
                      <Box height="1rem" width="100%" bgcolor="#EEE" />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
            {!props?.isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" style={{ padding: '5rem'}}>
                  Nenhum registro encontrado.
                </TableCell>
              </TableRow>
            )}
            {!props?.isLoading && (
              rows.map((row, index) => {
                return (
                  <TableRow hover key={index}>
                    {columns.map(column => {
                      return (
                        <TableCell
                          className={[
                            (props?.isSingleLine || column?.isSingleLine) && classnames.singleLineRow,
                            (column?.maxWidth && classnames.clampRow)
                          ]}
                          align={column.align || "left"}
                          key={column.key}
                          title={typeof row[column.key] === 'string' ? row[column.key] : ''}
                          style={{
                            maxWidth: column?.maxWidth ? column.maxWidth : 'auto',
                            width: column?.width ? column.width : 'auto',
                          }}
                        >
                          {typeof column?.renderCustomRow === 'function' ? column.renderCustomRow(row) : (row[column.key] || column?.defaultValue)}
                        </TableCell>)
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {(rows.length > 0 && showPagination) && (
        <Box display="flex" justifyContent="center">
          <TablePagination
            rowsPerPageOptions={tableRowsPerPage}
            component="div"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            labelRowsPerPage="Registros por página: "
            count={props?.totalRecords}
            rowsPerPage={props?.rowsPerPage}
            page={props?.page}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={handleRowsPageChange}
          />
        </Box>
      )}
    </Paper>
  )
}